/**
 * Wattec Custom PC Builder — step guide
 *
 * Turns Easify's own real option list into a guided, one-step-at-a-time
 * flow — without ever touching a selection itself. This only ever shows or
 * hides Easify's own real group containers (a class this file owns,
 * separate from Easify's own "tpo_hidden", so the two never conflict) and
 * lets the customer click a nav item or Next/Back to change which one is
 * showing. Every choice made here is the customer's own click landing
 * directly on Easify's own real control; the only code that ever sets a
 * selection is the load-a-saved-build feature in wattec-pc-save.js, which
 * goes through the adapter.
 */
(function () {
  'use strict';

  /* Which family each section belongs to, and the order both families and
     sections within them are worked through. Peripherals sit last, after
     the PC itself is fully specified. */
  var FAMILIES = [
    { key: 'start',       title: 'Start here',        sections: ['case_size', 'case'] },
    { key: 'core',        title: 'Core hardware',     sections: ['platform', 'motherboard', 'cpu', 'memory', 'gpu', 'storage', 'psu'] },
    { key: 'style',       title: 'Cooling & style',   sections: ['cooling', 'fans'] },
    { key: 'finish',      title: 'Finishing touches', sections: ['os'] },
    // Monitor, keyboard, mouse and headphones are add-ons to a finished
    // build rather than part of the PC itself, so they come last, grouped
    // as Extras — along with anything else that doesn't map to a known
    // section, which lands in the catch-all 'extras' section.
    { key: 'extras',      title: 'Extras',            sections: ['monitor', 'keyboard', 'mouse', 'headphones', 'extras'] }
  ];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function Guide(adapter, anchor) {
    this.aris = adapter;
    this.anchor = anchor;
    this.activeKey = null;
    this.steps = [];
  }

  Guide.prototype.start = function () {
    var sections = {};
    this.aris.discoverSections().forEach(function (section) { sections[section.key] = section; });

    var self = this;
    FAMILIES.forEach(function (family) {
      family.sections.forEach(function (key) {
        if (sections[key]) {
          self.steps.push({ section: sections[key], family: family });
          delete sections[key];
        }
      });
    });
    // Anything not covered above still gets a step, so nothing is ever lost
    // if Easify ever adds a section this file doesn't know about yet.
    Object.keys(sections).forEach(function (key) {
      self.steps.push({ section: sections[key], family: FAMILIES[FAMILIES.length - 1] });
    });

    this.buildRail();
    this.mount();
    this.activeKey = this.firstVisibleKey();
    this.sync();
    this.openStepPicker();

    this.aris.observeChanges(function () { self.sync(); });
  };

  /* What Wattec wants every customer to know about a build, shown in the
     rail under the picture. Each is a plain statement with a line-drawn
     icon — drawn from simple shapes rather than a loaded icon font, so
     they inherit the brand colour and cost nothing to load. */
  function icon(paths) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths + '</svg>';
  }

  var TRUST_BADGES = [
    {
      text: 'Professionally assembled',
      // screwdriver and bolt
      svg: icon('<path d="M14 4l6 6-3 3-6-6z"/><path d="M11 7L4 14v6h6l7-7"/>')
    },
    {
      text: 'Stress tested before it ships',
      // a reading climbing across a dial
      svg: icon('<path d="M3 13a9 9 0 0 1 18 0"/><path d="M12 13l4-3"/><path d="M3 20h18"/>')
    },
    {
      text: 'Clean cable management',
      // a cable routed in a tidy loop
      svg: icon('<path d="M5 4v7a4 4 0 0 0 4 4h6a4 4 0 0 1 4 4v1"/><circle cx="5" cy="4" r="1.6"/><circle cx="19" cy="20" r="1.6"/>')
    },
    {
      text: 'Drivers & BIOS updated',
      // a chip with an arrow coming down into it
      svg: icon('<rect x="7" y="11" width="10" height="9" rx="1"/><path d="M12 3v5"/><path d="M9.5 5.5L12 8l2.5-2.5"/><path d="M4 14h3M17 14h3"/>')
    },
    {
      text: '3 year warranty',
      // shield with a tick
      svg: icon('<path d="M12 3l7 3v5c0 4.4-3 8.3-7 10-4-1.7-7-5.6-7-10V6z"/><path d="M9 12l2 2 4-4"/>')
    },
    {
      text: 'Secure payment',
      // padlock
      svg: icon('<rect x="5" y="10" width="14" height="10" rx="1.5"/><path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10"/>')
    }
  ];
  /**
   * Builds the right-hand rail and moves into it the things that belong
   * beside the build rather than inside the step flow: the picture of the
   * case being configured, the build check, the finance line and the real
   * Add to Cart button.
   *
   * Everything moved here is moved, not copied, and the rail itself sits
   * inside the product form, so the Add to Cart button is still the form's
   * own submit control in its own form — which is what lets a customer's
   * click on it count as a real one.
   */
  Guide.prototype.buildRail = function () {
    var buttons = document.querySelector('.product-form__buttons');
    if (!buttons || buttons.querySelector('.wpg-rail')) return;

    var rail = el('div', 'wpg-rail');

    var figure = el('div', 'wpg-rail__figure');
    this.caseImage = el('img', 'wpg-rail__image');
    this.caseImage.alt = '';
    this.caseImage.loading = 'lazy';
    figure.appendChild(this.caseImage);
    this.caseCaption = el('div', 'wpg-rail__caption', '');
    figure.appendChild(this.caseCaption);
    rail.appendChild(figure);

    var summary = document.querySelector('.wpb-summary');
    if (summary) rail.appendChild(summary);

    var finance = document.querySelector('.finance-simple-eco');
    if (finance) rail.appendChild(finance);

    // The running build total ("Subtotal inc. taxes", from the product
    // template's "Wattec: build total" block) directly above Add to Cart,
    // so the price and the button are read together.
    var total = buttons.querySelector('.wt-atc-row__total');
    if (total) rail.appendChild(total);

    var submit = buttons.querySelector('.product-form__submit');
    if (submit) rail.appendChild(submit);

    // The theme's own wishlist button (and, while it is still installed, the
    // Wishlist by Square app's) sits under Add to Cart in the rail.
    var wishlist = document.querySelector('.product__info-wrapper .dT_VProdWishList') || buttons.querySelector('.Vtl-WishlistButtonWrapper');
    if (wishlist) rail.appendChild(wishlist);

    // The "Buy now. Pay later." box, under Add to Cart and the wishlist button
    // as on the pre-built product pages. Its hidden Shop Pay data element
    // stays behind where Liquid drew it: the rail sits inside the product
    // form, and that element carries a little form of its own, which must not
    // end up nested inside this one. assets/wattec-bnpl.js finds it there.
    var financeBox = document.querySelector('.product__info-wrapper [data-wt-bnpl]');
    if (financeBox) {
      var official = financeBox.querySelector('.wt-bnpl__official');
      if (official) financeBox.parentNode.insertBefore(official, financeBox);
      rail.appendChild(financeBox);
    }

    // The customer rating badge (the "Wattec: customer rating" block on the
    // product template), under the wishlist as it sits on the pre-built
    // product pages. Moved, not copied, so its rotating quotes keep going.
    var rating = document.querySelector('.product__info-wrapper [data-wt-rating]');
    if (rating) rail.appendChild(rating);

    var badges = el('ul', 'wpg-badges');
    TRUST_BADGES.forEach(function (badge) {
      var item = el('li', 'wpg-badges__item');
      var mark = el('span', 'wpg-badges__icon');
      mark.innerHTML = badge.svg;
      item.appendChild(mark);
      item.appendChild(el('span', 'wpg-badges__text', badge.text));
      badges.appendChild(item);
    });
    rail.appendChild(badges);

    buttons.appendChild(rail);
    this.rail = rail;
    this.updateCaseImage();
  };

  /**
   * Keeps the rail's picture on whichever case the customer has chosen, so
   * they are looking at the machine they are actually building. Before a
   * case is picked it falls back to the product's own photo.
   */
  Guide.prototype.updateCaseImage = function () {
    if (!this.caseImage) return;
    var chosenCase = null;
    this.aris.getSelection().forEach(function (item) {
      if (item.visible && item.section === 'case' && item.image) chosenCase = item;
    });

    var fallback = '';
    var configNode = document.getElementById('wattec-pc-builder-config');
    if (configNode) {
      try { fallback = (JSON.parse(configNode.textContent) || {}).featuredImage || ''; } catch (error) { fallback = ''; }
    }

    var src = chosenCase ? chosenCase.image : fallback;
    if (src && this.caseImage.getAttribute('src') !== src) this.caseImage.setAttribute('src', src);
    this.caseImage.hidden = !src;
    this.caseCaption.textContent = chosenCase ? chosenCase.label : 'Your case will appear here once chosen';
    this.caseCaption.classList.toggle('is-placeholder', !chosenCase);
    this.syncCaseImageProperty(chosenCase ? chosenCase.image : '');
  };

  /**
   * Sends the chosen case's picture along with the build, as a hidden line
   * item property ("_case_image": the leading underscore keeps it out of
   * the list of options customers see). The cart drawer, the cart page and
   * the added-to-cart notice show that picture for the line instead of the
   * base product's stock photo, so the cart shows the PC being bought.
   *
   * The field sits directly in the product form, outside Easify's own option
   * list, and is removed again whenever no case is chosen.
   */
  Guide.prototype.syncCaseImageProperty = function (image) {
    var buttons = document.querySelector('.product-form__buttons');
    var form = buttons && buttons.closest('form');
    if (!form) return;
    var field = form.querySelector('input[name="properties[_case_image]"]');
    var url = String(image || '').trim();
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (!/^https:\/\//i.test(url)) url = '';
    this.syncFloatingBarImage(url);
    if (!url) {
      if (field) field.remove();
      return;
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[_case_image]';
      form.appendChild(field);
    }
    if (field.value !== url) field.value = url;
  };

  /**
   * The floating Add to Cart bar's thumbnail follows the chosen case too, so
   * the picture beside the price is the PC being built. The product's own
   * photo (and its responsive sizes) is remembered and put back if the case
   * is cleared.
   */
  Guide.prototype.syncFloatingBarImage = function (url) {
    var img = document.querySelector('#wattec-bar .wb-thumb img');
    if (!img) return;
    if (!img.hasAttribute('data-wt-original-src')) {
      img.setAttribute('data-wt-original-src', img.getAttribute('src') || '');
      img.setAttribute('data-wt-original-srcset', img.getAttribute('srcset') || '');
    }
    if (url) {
      if (img.getAttribute('src') === url) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.setAttribute('src', url);
      img.classList.add('is-case');
    } else if (img.classList.contains('is-case')) {
      var srcset = img.getAttribute('data-wt-original-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', img.getAttribute('data-wt-original-src'));
      img.classList.remove('is-case');
    }
  };
  Guide.prototype.mount = function () {
    /* On a phone the full step list runs to most of a screen before the
       customer reaches the components themselves, so there it collapses
       behind this button and the Back/Next row carries the progress
       instead. On a desktop the button is hidden and the list is simply
       always open. */
    this.navToggle = el('button', 'wpg-navtoggle');
    this.navToggle.type = 'button';
    this.navToggle.setAttribute('aria-expanded', 'false');

    this.nav = el('div', 'wpg-nav');
    this.stepInfo = el('div', 'wpg-stepinfo');
    var controls = el('div', 'wpg-controls');
    this.backBtn = el('button', 'wpg-btn wpg-btn--back', '← Back');
    this.backBtn.type = 'button';
    this.nextBtn = el('button', 'wpg-btn wpg-btn--next', 'Next →');
    this.nextBtn.type = 'button';
    controls.appendChild(this.backBtn);
    controls.appendChild(this.stepInfo);
    controls.appendChild(this.nextBtn);

    var self = this;
    this.backBtn.addEventListener('click', function () { self.step(-1); });
    this.nextBtn.addEventListener('click', function () { self.step(1); });

    var shell = el('div', 'wpb wpg');
    shell.appendChild(this.navToggle);
    shell.appendChild(this.nav);
    shell.appendChild(controls);
    this.anchor.parentNode.insertBefore(shell, this.anchor);
    this.shell = shell;

    this.navToggle.addEventListener('click', function () {
      var open = shell.classList.toggle('is-nav-open');
      self.navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      self.renderNavToggle();
    });

    /* One listener on the list itself rather than one per step button.
       The nav redraws whenever the build changes, and a button that is
       replaced between a finger going down and coming up takes its own
       listener with it — on a tablet that showed up as taps on a step
       doing nothing at all. The list is never replaced, so this always
       catches them. */
    this.nav.addEventListener('click', function (event) {
      var item = event.target.closest ? event.target.closest('.wpg-nav__item') : null;
      if (!item || !item.dataset || !item.dataset.step) return;
      shell.classList.remove('is-nav-open');
      self.navToggle.setAttribute('aria-expanded', 'false');
      self.goTo(item.dataset.step);
    });
  };

  /** The collapsed nav's own label: where the customer is, and the way in. */
  Guide.prototype.renderNavToggle = function () {
    if (!this.navToggle) return;
    var steps = this.visibleSteps();
    var index = steps.map(function (s) { return s.section.key; }).indexOf(this.activeKey);
    var open = this.shell && this.shell.classList.contains('is-nav-open');
    var where = index === -1 ? '' : ' — step ' + (index + 1) + ' of ' + steps.length;
    this.navToggle.textContent = (open ? 'Hide all steps' : 'All build steps') + where;
  };

  /** Every step that currently has at least one option Easify is showing —
   *  a step with nothing to show yet (its prerequisite not chosen) is left
   *  out of the nav rather than presented as an empty page. */
  Guide.prototype.visibleSteps = function () {
    var self = this;
    return this.steps.filter(function (step) {
      return step.section.options.some(function (option) { return self.aris.isVisible(option); });
    });
  };

  Guide.prototype.firstVisibleKey = function () {
    var steps = this.visibleSteps();
    return steps.length ? steps[0].section.key : null;
  };

  Guide.prototype.activeStep = function () {
    var self = this;
    return this.steps.filter(function (step) { return step.section.key === self.activeKey; })[0];
  };

  Guide.prototype.goTo = function (key) {
    this.activeKey = key;
    this.sync();
    this.scrollToStep();
    this.openStepPicker();
  };

  /**
   * Arriving at a component step, its list of components is already open,
   * so the customer is looking at the parts rather than at a closed field
   * they have to find and tap first.
   *
   * It opens the list the same way a customer would — by pressing the
   * field — so Easify keeps control of it from there: choosing a part
   * closes it, and pressing the field again opens or closes it as usual.
   * This runs only when the step changes, never on the regular re-reads of
   * the build, so a list the customer has deliberately closed stays closed.
   */
  Guide.prototype.openStepPicker = function () {
    var self = this;
    var step = this.activeStep();
    if (!step) return;

    window.setTimeout(function () {
      if (self.activeStep() !== step) return;
      var showing = step.section.options.filter(function (option) {
        return self.aris.isVisible(option);
      });
      // A step whose list is already open is left exactly as it is.
      if (showing.some(function (option) { return self.aris.isPickerOpen(option); })) return;
      for (var i = 0; i < showing.length; i++) {
        if (self.aris.openPicker(showing[i])) return;
      }
    }, 260);
  };

  /**
   * Brings the step's own controls to the top of the screen. Scrolling to
   * the option list itself overshot it: changing step changes how tall the
   * page is (the other steps collapse away), so a smooth scroll started
   * before that settled would land past its target. This measures after
   * the change has been applied, scrolls to the step bar rather than the
   * list, and leaves room for the sticky header sitting over the top.
   */
  Guide.prototype.scrollToStep = function () {
    var shell = this.shell;
    if (!shell) return;
    window.setTimeout(function () {
      var header = document.querySelector('.header-wrapper, header, .section-header');
      var offset = header ? header.getBoundingClientRect().height : 0;
      var top = shell.getBoundingClientRect().top + window.pageYOffset - offset - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 60);
  };

  Guide.prototype.step = function (direction) {
    var steps = this.visibleSteps();
    var index = steps.map(function (s) { return s.section.key; }).indexOf(this.activeKey);
    var next = index + direction;
    if (next < 0 || next >= steps.length) return;
    this.goTo(steps[next].section.key);
  };

  /** Whether every *required* option in a step already has a visible
   *  selection — optional options (Additional Storage, the peripherals)
   *  never block this, matching Easify's own required-field rule exactly. */
  /* Easify pre-fills the optional groups with a placeholder ("Default",
     "No Thanks - Skip Monitor") so the field is never blank. That is not
     the customer having chosen something, so it does not tick the step. */
  var PLACEHOLDER_STARTS = ['default', 'none', 'no thanks', 'no custom', 'skip'];

  function isPlaceholder(item) {
    if (item.price) return false;
    var label = String(item.label || '').trim().toLowerCase();
    for (var i = 0; i < PLACEHOLDER_STARTS.length; i++) {
      if (label.indexOf(PLACEHOLDER_STARTS[i]) === 0) return true;
    }
    return false;
  }

  /**
   * Whether a step is done. A step with required options is done once each
   * of them has a real answer. A step with none — the peripherals, extra
   * storage — is only done once the customer has actually picked something
   * in it: before that it was ticking itself the moment the page loaded,
   * because "no required options left unanswered" is trivially true of a
   * step that has none.
   */
  Guide.prototype.stepAnswered = function (step) {
    var self = this;
    var chosen = {};
    this.aris.getSelection().forEach(function (item) {
      if (item.visible && !isPlaceholder(item)) chosen[item.optionKey] = true;
    });

    var showing = step.section.options.filter(function (option) { return self.aris.isVisible(option); });
    var required = showing.filter(function (option) { return option.required; });

    if (required.length) {
      return required.every(function (option) { return chosen[option.key]; });
    }
    return showing.some(function (option) { return chosen[option.key]; });
  };

  Guide.prototype.sync = function () {
    var self = this;
    var steps = this.visibleSteps();
    var keys = steps.map(function (s) { return s.section.key; });
    if (keys.indexOf(this.activeKey) === -1) this.activeKey = keys[0] || null;

    // Show only the active step's own real group(s); hide every other
    // step's, regardless of what Easify itself currently thinks is visible
    // — Easify's own tpo_hidden still narrows the active step down to
    // whichever single group actually applies.
    this.steps.forEach(function (step) {
      var isActive = step.section.key === self.activeKey;
      step.section.options.forEach(function (option) {
        var element = self.aris.elementFor(option);
        if (!element) return;
        element.classList.toggle('wpb-step-hidden', !isActive);
      });
    });

    this.renderNav(steps);
    this.renderNavToggle();

    var index = keys.indexOf(this.activeKey);
    var active = this.activeStep();
    if (active) {
      var required = active.section.options.some(function (o) { return o.required; });
      this.stepInfo.innerHTML = '';
      this.stepInfo.appendChild(el('span', 'wpg-stepinfo__pos', (index + 1) + ' / ' + steps.length));
      this.stepInfo.appendChild(el('span', 'wpg-stepinfo__title', active.section.title));
      if (!required) this.stepInfo.appendChild(el('span', 'wpg-stepinfo__optional', 'Optional'));
    }

    this.updateCaseImage();

    this.backBtn.disabled = index <= 0;
    this.nextBtn.disabled = index === -1 || index >= steps.length - 1;
    this.nextBtn.classList.toggle('wpg-btn--waiting', !!(active && !this.stepAnswered(active)));
  };

  Guide.prototype.renderNav = function (steps) {
    var self = this;

    /* Redraw only when something a customer would see has actually
       changed. The build is re-read several times a second, and redrawing
       the list every time meant its buttons were constantly being
       replaced underneath whoever was trying to press one. */
    var signature = steps.map(function (step) {
      return step.section.key + (self.stepAnswered(step) ? '1' : '0') +
        (step.section.key === self.activeKey ? 'A' : '');
    }).join('|');
    if (signature === this.navSignature) return;
    this.navSignature = signature;

    this.nav.innerHTML = '';
    var byFamily = {};
    var order = [];
    steps.forEach(function (step) {
      var fk = step.family.key;
      if (!byFamily[fk]) { byFamily[fk] = []; order.push(step.family); }
      byFamily[fk].push(step);
    });

    var seen = {};
    order.forEach(function (family) {
      if (seen[family.key]) return;
      seen[family.key] = true;
      var group = el('div', 'wpg-nav__group');
      group.appendChild(el('div', 'wpg-nav__group-title', family.title));
      var list = el('div', 'wpg-nav__list');
      byFamily[family.key].forEach(function (step) {
        var answered = self.stepAnswered(step);
        var item = el('button', 'wpg-nav__item' +
          (step.section.key === self.activeKey ? ' is-active' : '') +
          (answered ? ' is-done' : ''));
        item.type = 'button';
        item.textContent = step.section.title;
        // Which step this is; the list's own listener reads it back.
        item.dataset.step = step.section.key;
        list.appendChild(item);
      });
      group.appendChild(list);
      self.nav.appendChild(group);
    });
  };

  function boot() {
    if (!window.WattecPCBuilder || !window.WattecPCBuilder.aris) return;
    var adapter = window.WattecPCBuilder.aris;

    // Easify renders its own container with JavaScript, well after this
    // page's own load event — so the anchor is only looked for once the
    // adapter reports Easify's controls are actually on the page. Looking
    // for it any earlier finds nothing and silently gives up.
    adapter.ready().then(function () {
      var anchor = document.querySelector('.tpo_option-set-container');
      if (!anchor) return;
      var guide = new Guide(adapter, anchor);
      guide.start();
      window.WattecPCBuilder.guide = guide;
    }).catch(function (error) {
      if (window.console) console.warn('[Wattec guide]', error.message);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
